import React, {useEffect, useState} from "react";
import {Button, Input, notification, Select, Spin, Table, Tooltip} from "antd";
import {useNavigate} from "react-router-dom";
import SharedService from "../../../../services/shared/service";
import {useAuth} from "oidc-react";
import SEO from "../../../../components/SEO"
import {EyeOutlined, SearchOutlined} from "@ant-design/icons";

const pageSEOSettings = {
    title: "Supplier List",
};

const AdminSupplier = () => {
    const {userData, userManager} = useAuth();
    const token = userData?.access_token;
    const navigate = useNavigate();

    const [supplierData, setSupplierData] = useState([]);
    const [supplierSearch, setSupplierSearch] = useState("");
    const [countryList, setCountryList] = useState([]);
    const [countryItem, setCountryItem] = useState("");
    const [supplierLoading, setSupplierLoading] = useState(false);
    const [sortedInfo, setSortedInfo] = useState({});
    const [searchTrigger, setSearchTrigger] = useState(0);
    const [listPagination, setlistPagination] = useState({
        totalItemCount: 0,
        pageSize: 10,
        pageNumber: 1,
    });

    useEffect(() => {
        getCountries();
    }, []);

    useEffect(() => {
        fetchSupplierData();
    }, [listPagination.pageNumber, sortedInfo, searchTrigger]);

    const getCountries = async () => {
        try {
            const response = await SharedService.get({
                token,
                url: "reference/countries",
            });
            const countries = Array.isArray(response)
                ? response
                : response?.result ?? [];

            setCountryList(
                countries.map((item) => ({
                    label: item.name,
                    value: item.id,
                }))
            );
        } catch (error) {
            notification.error({
                message: "Error",
                description: error?.response?.statusText,
            });
        }
    };

    const handleColumnClick = (record) => {
        navigate(`/admin/suppliers/details/${record}`);
    };
    const columns = [
        {
            title: "Sr. No",
            dataIndex: "",
            key: "serialNo",
            render: (text, record, index) => {
                if (listPagination.pageNumber !== 1) {
                    return (listPagination.pageNumber - 1) * 10 + index + 1;
                }
                return index + 1;
            },
            width: 100,
        },
        {
            title: "Supplier Name",
            dataIndex: "name",
            render: (name) => {
                return (
                    <Tooltip title={name}>
                        <div>
                            {name && name.length > 50 ? `${name.slice(0, 50)}...` : name}
                        </div>
                    </Tooltip>
                );
            },
            key: "name",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "name" && sortedInfo.order,
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "type" && sortedInfo.order,
            render: (type) => {
                return (
                    <Tooltip title={type}>
                        <div>
                            {type.map((item, index) => (
                                <span key={index}>
                  {item}
                                    {index !== type.length - 1 && ", "}
                </span>
                            ))}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: "Currency",
            dataIndex: "companyAllowedCurrencies",
            key: "companyAllowedCurrencies",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "companyAllowedCurrencies" && sortedInfo.order,
            render: (Currency) => (
                <Tooltip title={Currency.map((item) => item.value).join(", ")}>
                    <div>
                        {Currency.map((currency, index) => (
                            <span className={index !== 0 ? "mt-2" : ""} key={currency.value}>
                {currency.value}
                                {index !== Currency.length - 1 && <br/>}{" "}
                                {/* Add <br /> if not the last item */}
              </span>
                        ))}
                    </div>
                </Tooltip>
            ),
        },

        {
            title: "Country",
            dataIndex: "address",
            key: "address.country",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "address.country" && sortedInfo.order,
            render: (address) => (
                <Tooltip title={address.countryName}>
          <span>
            {address.countryName} ({address.country})
          </span>
                </Tooltip>
            ),
        },
        {
            title: <div className="flex items-center justify-center">Is Vetted</div>,
            dataIndex: "isVetted",
            key: "isVetted",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "isVetted" && sortedInfo.order,
            render: (isVetted) => (
                <Tooltip title={isVetted ? "Yes" : "No"}>
          <span className="flex items-center justify-center">
            {isVetted ? "Yes" : "No"}
          </span>
                </Tooltip>
            ),
        },
        {
            title: "Rounding",
            dataIndex: "permitInvoiceRounding",
            key: "permitInvoiceRounding",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "permitInvoiceRounding" && sortedInfo.order,

            render: (permitInvoiceRounding) => (
                <Tooltip title={permitInvoiceRounding ? "Yes" : "No"}>
          <span className="flex items-center justify-center">
            {permitInvoiceRounding ? "Yes" : "No"}
          </span>
                </Tooltip>
            ),
        },
        {
            title: "Recovery Types",
            dataIndex: "recoveryServiceTypes",
            key: "recoveryServiceTypes",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "recoveryServiceTypes" && sortedInfo.order,

            render: (RecoveryTypes) => (
                <Tooltip title={RecoveryTypes.map((item) => item.value).join(", ")}>
                    <div>
                        {RecoveryTypes.map((item, index) => (
                            <span className={index !== 0 ? "mt-2" : ""} key={item.value}>
                {item.value}
                                {index !== RecoveryTypes.length - 1 && <br/>}{" "}
                                {/* Add <br /> if not the last item */}
              </span>
                        ))}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: "INVEmail",
            dataIndex: "isCompanyInvoiceSupplier",
            key: "isCompanyInvoiceSupplier",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "isCompanyInvoiceSupplier" && sortedInfo.order,
            render: (email) => (
                <Tooltip title={email ? "Yes" : "NO"}>
          <span className="flex items-center justify-center">
            {email ? "Yes" : "No"}
          </span>
                </Tooltip>
            ),
        },
        {
            title: "Correspondence Email",
            dataIndex: "correspondenceEmail",
            key: "correspondenceEmail",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "correspondenceEmail" && sortedInfo.order,
            render: (email) => (
                <Tooltip title={email}>
                    <span>{email} </span>
                </Tooltip>
            ),
        },
        {
            title: "Invoice Emails ",
            dataIndex: "companyInvoiceEmails",
            key: "companyInvoiceEmails",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "companyInvoiceEmails" && sortedInfo.order,

            render: (email) => (
                <Tooltip title={email.map((item) => item.value).join(", ")}>
                    <div>
                        {email.map((emailItem, index) => (
                            <span className={index !== 0 ? "mt-2" : ""} key={emailItem.value}>
                {emailItem.value}
                                {index !== emailItem.length - 1 && <br/>}{" "}
                                {/* Add <br /> if not the last item */}
              </span>
                        ))}
                    </div>
                </Tooltip>
            ),
        },

        {
            title: "Actions",
            align: 'center',
            render: (record) => (
                <Tooltip title="View">
                    <EyeOutlined
                        style={{fontSize: 20}}
                        onClick={() => handleColumnClick(record)}
                        className="cursor-pointer"
                    />
                </Tooltip>
            ),
            width: 100,
            dataIndex: "id",
            key: "Actions",
        },
    ];
    const tableConfig = {
        rowKey: "id",
    };
    const paginationProps = {
        showSizeChanger: false,
        total: listPagination.totalItemCount,
        pageSize: listPagination.pageSize,
        current: listPagination.pageNumber,
    };
    const handleTableChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
        setlistPagination({...listPagination, pageNumber: pagination.current});
    };

    const fetchSupplierData = async () => {
        setSupplierLoading(true);
        try {
            const data = {
                ...(supplierSearch !== null && supplierSearch !== "" && {name: supplierSearch}),
                ...(countryItem !== null && countryItem !== "" && {country: countryItem})
            };

            const params = {
                pageNumber: listPagination.pageNumber,
                pageSize: listPagination.pageSize,
                select: 'id,name,email,companyAllowedCurrencies,correspondenceEmail,companyInvoiceEmails,type,recoveryServiceTypes,isVetted,invoiceEmail,address,permitInvoiceRounding',
                orderBy: sortedInfo.order
                    ? `${sortedInfo.columnKey}:${sortedInfo.order === "ascend" ? "Asc" : "Desc"
                    }`
                    : undefined,
            };
            const response = await SharedService.post({
                token,
                url: "suppliers",
                data,
                params,
            });
            if (response) {
                setSupplierData(response.items);
                setlistPagination((prev) => ({
                    ...prev,
                    totalItemCount: response.totalItemCount,
                }));
                setSupplierLoading(false);
            }
        } catch (error) {
            setSupplierLoading(false);
            if (error.response && error.response.status === 500) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
            if (error.response && error.response.status === 502) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
        }
    };

    const runSearch = () => {
        setlistPagination((prev) => ({...prev, pageNumber: 1}));
        setSearchTrigger((n) => n + 1);
    };

    const handleSupplier = (e) => {
        setSupplierSearch(e.target.value);
        if (!e.target.value) {
            runSearch();
        }
    };

    return (
        <div>
            <SEO {...pageSEOSettings} />
            <div className="px-4 sm:px-7 py-5">
                <div className="text-[22px] font-semibold text-[#0B1220] leading-tight">Suppliers</div>
                <div className="text-[13px] text-[#7A849A] mt-1">
                    Manage supplier records, countries and currencies.
                </div>
            </div>

            <div className="px-4 sm:px-7 pb-7 relative">
                {supplierLoading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10 rounded-[14px]">
                        <Spin size="large" className="custom-spin"/>
                    </div>
                )}

                <div className="flex items-center gap-3 flex-wrap mb-4">
                    <Input
                        placeholder="Search supplier name…"
                        prefix={<SearchOutlined className="text-[#7A849A]"/>}
                        className="w-[280px]"
                        value={supplierSearch}
                        allowClear
                        onChange={handleSupplier}
                        onPressEnter={runSearch}
                    />
                    <Select
                        allowClear
                        placeholder="All countries"
                        value={countryItem || undefined}
                        className="w-[220px]"
                        options={countryList}
                        onChange={(value) => setCountryItem(value ?? "")}
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined/>}
                        onClick={runSearch}
                    >
                        Search
                    </Button>
                </div>

                <Table
                    {...tableConfig}
                    pagination={paginationProps}
                    columns={columns}
                    dataSource={supplierData}
                    onChange={handleTableChange}
                    scroll={{x: 960}}
                />
            </div>
        </div>
    );
};

export default AdminSupplier;
